// src/components/IncomingOrders/OrderInspector.jsx

import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useLazyQuery } from "@apollo/client";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendIcon from "@mui/icons-material/Send";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";

import { PRINT_SUPPLIER_ORDERS } from "../../graphql/queries/IncomingOrdersContext";
import { getCurrentCompanyId } from "../../helpers/selectors";
import SplitButton from "../common/SplitButton";
import { ACTIONS } from "./helpers/actions";
import { useAddressLabels } from "./hooks/useAddressLabels";
import { useEditableOrder } from "./hooks/useEditableOrder";
import OrderDetail from "./OrderDetail";
import ReplyModal from "./ReplyModal";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const saveOptions = [
  {
    //type: "", (Skicka ingen type för vanlig spara med ev. action)
    labelId: "incomingOrders.order.saveWithAction",
    label: null,
    icon: (
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 20,
          height: 20,
        }}
      >
        <SaveOutlinedIcon fontSize="small" />

        {/* Premium badge */}
        <Box
          sx={{
            position: "absolute",
            bottom: -3,
            right: -3,
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: "#FACC15", // mjuk premium-gul
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
          }}
        >
          <BoltOutlinedIcon
            sx={{
              fontSize: 14,
              color: "#1E293B",
            }}
          />
        </Box>
      </Box>
    ),
  },
  {
    type: "NONE",
    labelId: "incomingOrders.order.saveWithoutAction",
    label: null,
    icon: <SaveOutlinedIcon fontSize="small" />,
  },
];

const MANUAL_ACTIONS = Object.keys(ACTIONS).filter(
  (a) => a !== "NONE"
);

// TODO: koppla till din verkliga data
const getManualActions = (order) => {
  // Exempel: om du senare lägger på order.manualActions = ["SEND_ORDER_CONFIRMATION", ...]
  // return order?.manualActions || [];

  // Tillfälligt: visa alla möjliga för demo
  return MANUAL_ACTIONS;
};

const MIN_WIDTH = 600;
const MAX_WIDTH = 900;
const INITIAL_WIDTH = MIN_WIDTH;

const paperWidthConstraints = {
  minWidth: MIN_WIDTH,
  maxWidth: "90vw",
};

const headerIconSx = {
  width: 32,
  height: 32,
  color: "text.secondary",
  opacity: 0.85,
  "&:hover": { opacity: 1 },
};

const OrderInspector = ({
  order,
  orderError,
  orderLoading,
  orderUpdating,
  orderUpdateError,
  onClose,
  onUpdateOrder,
  onWidthChange,
  width = INITIAL_WIDTH,
  onOpenStatusManager,
  availableUsers,
  currentSelectedUser,
  setCurrentSelectedUser,
  tableSettings
}) => {
  const intl = useIntl();
  const [addressView, setAddressView] = useState(() => {
    try {
      const v = localStorage.getItem("orderAddressView");
      if (v === "compact" || v === "compare" || v === "full") return v;
      return "compact";
    } catch {
      return "compact";
    }
  });

  const {
    editableOrder,
    getPayload,
    isDirty,
    isFieldDirty,
    isObjectDirty,
    resetDirty,
    updateField,
  } = useEditableOrder(order);

  const { getLabel: getAddressLabel } = useAddressLabels(order?.sortedAddressFields)

  const [printSupplierOrders, { data: supplierOrdersPrintData }] = useLazyQuery(
    PRINT_SUPPLIER_ORDERS
  );

  const supplierCompanyId = useSelector(getCurrentCompanyId);

  const handlePrintOrderDetails = () => {
    const id = order?.supplierOrderId;
    if (!id) return;

    printSupplierOrders({
      variables: {
        supplierCompanyId,
        supplierOrderIds: [id],
      },
    });
  };

  const handleSaveActions = (action) => {
    const supplierOrderId = order?.supplierOrderId;
    if (!supplierOrderId) return;

    const patch = getPayload();
    patch.performAction = action.type;


    onUpdateOrder({ supplierOrderId, patch });

  }

  const handleSave = () => {
    const supplierOrderId = order?.supplierOrderId;
    if (!supplierOrderId) return;

    const patch = getPayload();

    onUpdateOrder({ supplierOrderId, patch });
  };

  const [dragging, setDragging] = useState(false);
  const [internalWidth, setInternalWidth] = useState(width);
  const [deliveryNoticeSentAt, setDeliveryNoticeSentAt] = useState();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openMessageDialog, setOpenMessageDialog] = useState(false);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [executing, setExecuting] = useState(false);

  const manualActions = useMemo(() => getManualActions(order), [order]);
  const hasManualActions = manualActions?.length > 0;

  const openActionMenu = (e) => setActionAnchorEl(e.currentTarget);
  const closeActionMenu = () => setActionAnchorEl(null);

  const runActions = async (types) => {
    const supplierOrderId = order?.supplierOrderId;
    if (!supplierOrderId) return;

    try {
      setExecuting(true);

      // Kör sekventiellt, så du får deterministisk ordning
      for (const type of types) {

        // Om din backend inte vill ha NONE, hoppa över
        if (!type || type === "NONE") continue;

        // Här skickar vi en ren action-patch (utan att spara formulärfält)
        await onUpdateOrder({
          supplierOrderId,
          patch: { performAction: type },
        });
      }
    } finally {
      setExecuting(false);
      closeActionMenu();
    }
  };

  const handleRunOne = (type) => runActions([type]);
  const handleRunAll = () => runActions(manualActions);

  const handleSendDeliveryNotice = () => {
    setDeliveryNoticeSentAt(new Date());
    setSnackbarOpen(true);
  };

  useEffect(() => {
    try {
      localStorage.setItem("orderAddressView", addressView);
    } catch { }
  }, [addressView]);

  useEffect(() => {
    const printUrl = supplierOrdersPrintData?.incomingOrdersContext?.printSupplierOrders;
    if (!!printUrl) {
      window.open(printUrl);
    }
  }, [supplierOrdersPrintData]);

  // Håll intern bredd i synk med prop
  useEffect(() => {
    setInternalWidth(width);
  }, [width]);

  useEffect(() => {
    setDeliveryNoticeSentAt(order?.deliveryNoticeSentAt ?? null);
  }, [order]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleMouseUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (onWidthChange) {
      onWidthChange(internalWidth);
    }
  };

  const handleMouseMove = (event) => {
    if (!dragging) return;

    const newWidth = clamp(window.innerWidth - event.clientX, MIN_WIDTH, MAX_WIDTH);
    setInternalWidth(newWidth);
  };

  useEffect(() => {
    if (!dragging) return;

    document.body.style.userSelect = "none";
    document.body.style.cursor = "ew-resize";

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.body.style.userSelect = "";
      document.body.style.cursor = "";

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  const DeliveryConfirmedIcon = () => (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
      {/* Outline-version bakom */}
      <SendIcon
        sx={{
          position: "absolute",
          bottom: -2,
          right: -2,
          fontSize: 10,
          color: "transparent",
          "& path": {
            stroke: "white",
            strokeWidth: 9,
            strokeLinecap: "round",
            strokeLinejoin: "round",
          },
        }}
      />

      {/* Originalikonen ovanpå */}
      <SendIcon
        sx={{
          position: "absolute",
          bottom: -2,
          right: -2,
          fontSize: 10,
          color: "#16A34A",
        }}
      />

      {/* LocalShipping bakom allt */}
      <LocalShippingOutlinedIcon fontSize="small" />
    </Box>
  );

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        display: "flex",
        flexDirection: "row",
      }}
    >
      {/* Själva panelen med orderdetaljer */}
      <Paper
        elevation={2}
        sx={{
          width: internalWidth,
          ...paperWidthConstraints,
          height: "100%",
          p: 2,
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flexShrink: 0,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          {/* Vänster: titel + vyväljare */}
          <Stack direction="row" alignItems="center" gap={1}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="h6" fontWeight={700}>
                Orderdetaljer
              </Typography>

              {order?.supplierOrderNo != null && (
                <Typography variant="body2" color="text.secondary">
                  #{order.supplierOrderNo}
                </Typography>
              )}
            </Stack>

            {/* VYVÄLJARE */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                backgroundColor: "rgba(0,0,0,0.025)",
                borderRadius: 1,
                p: "1px",
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={addressView}
                onChange={(e, v) => v && setAddressView(v)}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: "none",
                    borderRadius: 1,
                    width: 24,
                    height: 20,
                    p: 0,
                    color: "text.secondary",
                    opacity: 0.65,
                  },
                  "& .MuiToggleButton-root svg": {
                    fontSize: "0.9rem",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "rgba(0,0,0,0.06)",
                    color: "text.primary",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.18)",
                    opacity: 1,
                  },
                  "& .Mui-selected:hover": {
                    backgroundColor: "background.paper",
                  },
                }}
              >
                <ToggleButton value="compact">
                  <ViewAgendaIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="compare">
                  <TableRowsIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="full">
                  <ViewColumnIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

            </Box>
          </Stack>

          {/* Höger: actions */}
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              "& > *": {
                ml: 1,
              },
              "& > *:first-of-type": {
                ml: 0,
              },
            }}
          >
            <SplitButton
              disabled={!isDirty || orderUpdating || orderLoading || orderError}
              onClick={handleSaveActions} options={saveOptions.map((option) => ({
                ...option,
                label: option.labelId ? intl.formatMessage({ id: option.labelId }) : option.label,
              }))}
              placement="bottom-end"
            />

            <Tooltip
              title={isDirty ? intl.formatMessage({ id: "incomingOrders.order.orderMustBeSavedFirst" }) : (hasManualActions ? intl.formatMessage({ id: "incomingOrders.order.executeAction" }) : intl.formatMessage({ id: "incomingOrders.order.noActionsAvailable" }))}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={openActionMenu}
                  disabled={isDirty || !hasManualActions || executing}
                  sx={[
                    headerIconSx,
                    {
                      ml: 1,
                      mr: -0.5,
                    },
                  ]}
                >
                  {executing ? (
                    <CircularProgress size={16} />
                  ) : (
                    <BoltOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>

            <Menu
              anchorEl={actionAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(actionAnchorEl)}
              onClose={closeActionMenu}
              slotProps={{ paper: { sx: { borderRadius: 2 } } }}
            >
              {manualActions.map((type) => {
                const { label, labelId, icon: Icon } = ACTIONS[type] ?? {};

                return (
                  <MenuItem
                    key={type}
                    onClick={() => handleRunOne(type)}
                    disabled={executing}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {Icon && <Icon fontSize="small" />}
                    </ListItemIcon>

                    <ListItemText primary={labelId ? intl.formatMessage({ id: labelId }) : label ?? type} />
                  </MenuItem>
                );
              })}
            </Menu>

            <Tooltip title={intl.formatMessage({ id: "incomingOrders.order.sendMessage" })} arrow>
              <IconButton
                size="small"
                // TODO: koppla in din befintliga handler här om du har en
                sx={headerIconSx}
                onClick={e => setOpenMessageDialog(true)}
              >
                <MailOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <ReplyModal supplierOrderId={order?.supplierOrderId} open={openMessageDialog} onClose={n => setOpenMessageDialog(false)} />

            <Tooltip title={intl.formatMessage({ id: "incomingOrders.order.printOrder" })} arrow>
              <IconButton
                size="small"
                onClick={handlePrintOrderDetails}
                sx={headerIconSx}
              >
                <PrintOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        {/* Innehåll */}
        <Box sx={{ flex: 1, overflow: "auto", pr: 1 }}>
          {editableOrder && !orderLoading && !orderError &&
            <OrderDetail
              order={editableOrder}
              width={width}
              addressView={addressView}
              setAddressView={setAddressView}
              updateField={updateField}
              isFieldDirty={isFieldDirty}
              isObjectDirty={isObjectDirty}
              getAddressLabel={getAddressLabel}
              onOpenStatusManager={onOpenStatusManager}
              availableUsers={availableUsers}
              currentSelectedUser={currentSelectedUser}
              setCurrentSelectedUser={setCurrentSelectedUser}
              tableSettings={tableSettings}
              panelWidth={internalWidth}
            />
          }
          {orderError && !orderLoading &&
            <Typography variant="body2" color="error">
              Kan inte visa detaljer för vald order.
            </Typography>
          }
          {orderLoading &&
            <Typography variant="body2" color="text.secondary">
              Hämtar order...
            </Typography>
          }
        </Box>
      </Paper>

      {/* Vertikalt draghandtag (resizable) */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: "absolute",
          left: -3,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: "ew-resize",
          backgroundColor: "#f7bd02",
          opacity: 0.6,
          "&:hover": { opacity: 1 },
          borderRadius: "4px",
        }}
      />
    </Box>
  );
};

OrderInspector.displayName = "OrderInspector";

export default OrderInspector;
